import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Play, 
  Pause, 
  Square,
  Megaphone,
  Users,
  Clock,
  Target,
  TrendingUp,
  MoreVertical,
  Trash2,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function Campaigns() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    agent_id: "",
    list_id: "",
    calling_hours_start: "09:00",
    calling_hours_end: "17:00",
    calls_per_minute: 1,
    max_attempts: 3
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, agentsRes, listsRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers: getAuthHeader() }),
        axios.get(`${API}/agents`, { headers: getAuthHeader() }),
        axios.get(`${API}/lists`, { headers: getAuthHeader() })
      ]);
      setCampaigns(campaignsRes.data);
      setAgents(agentsRes.data.filter(a => a.is_active));
      setLists(listsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name || !newCampaign.agent_id || !newCampaign.list_id) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const formData = new FormData();
      Object.entries(newCampaign).forEach(([k, v]) => {
        if (v !== "") formData.append(k, v);
      });
      
      await axios.post(`${API}/campaigns`, formData, { headers: getAuthHeader() });
      toast.success("Campaign created!");
      setShowCreateDialog(false);
      setNewCampaign({
        name: "", description: "", agent_id: "", list_id: "",
        calling_hours_start: "09:00", calling_hours_end: "17:00",
        calls_per_minute: 1, max_attempts: 3
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    }
  };

  const updateStatus = async (campaignId, status) => {
    try {
      // If starting campaign, initiate the dialer queue first
      if (status === "running") {
        const campaign = campaigns.find(c => c.id === campaignId);
        if (campaign) {
          // Create dialer queue
          try {
            await axios.post(`${API}/dialer/queue/create`, {
              campaign_id: campaignId,
              agent_id: campaign.agent_id,
              from_number: "+14165550000", // TODO: Get from business config
              list_id: campaign.list_id,
              calls_per_minute: campaign.calls_per_minute || 1,
              calling_hours_start: campaign.calling_hours_start || "09:00",
              calling_hours_end: campaign.calling_hours_end || "17:00",
              max_attempts: campaign.max_attempts || 3
            }, { headers: getAuthHeader() });
          } catch (queueError) {
            // Queue might already exist, continue
            console.log("Queue creation:", queueError.response?.data);
          }
          
          // Start the campaign dialer
          await axios.post(`${API}/dialer/campaign/${campaignId}/start`, {}, { headers: getAuthHeader() });
        }
      } else if (status === "paused") {
        await axios.post(`${API}/dialer/campaign/${campaignId}/pause`, {}, { headers: getAuthHeader() });
      } else if (status === "cancelled") {
        await axios.post(`${API}/dialer/campaign/${campaignId}/stop`, {}, { headers: getAuthHeader() });
      }
      
      // Update campaign status
      const formData = new FormData();
      formData.append("status", status);
      await axios.put(`${API}/campaigns/${campaignId}/status`, formData, { headers: getAuthHeader() });
      toast.success(`Campaign ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update campaign");
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!confirm("Delete this campaign?")) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}`, { headers: getAuthHeader() });
      toast.success("Campaign deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "running": return "bg-emerald-500";
      case "paused": return "bg-amber-500";
      case "completed": return "bg-blue-500";
      case "scheduled": return "bg-purple-500";
      case "cancelled": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const getProgress = (stats) => {
    if (!stats?.total_contacts) return 0;
    return Math.round((stats.contacted / stats.total_contacts) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce"></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></span>
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="campaigns-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Campaigns</h1>
          <p className="text-muted-foreground">Create and manage outbound calling campaigns</p>
        </div>
        <Button 
          className="rounded-full bg-gradient-to-r from-cyan-500 to-purple-500"
          onClick={() => setShowCreateDialog(true)}
          data-testid="create-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Campaigns</p>
              <p className="text-2xl font-bold">{campaigns.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Play className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{campaigns.filter(c => c.status === "running").length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Contacted</p>
              <p className="text-2xl font-bold">
                {campaigns.reduce((acc, c) => acc + (c.stats?.contacted || 0), 0)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Leads Generated</p>
              <p className="text-2xl font-bold">
                {campaigns.reduce((acc, c) => acc + (c.stats?.qualified || 0), 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      <div className="space-y-4">
        {campaigns.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first outbound calling campaign to start generating leads
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          campaigns.map((campaign) => (
            <Card key={campaign.id} className="bg-card border-border" data-testid={`campaign-${campaign.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-xl font-heading font-semibold">{campaign.name}</h3>
                      <Badge className={`${getStatusColor(campaign.status)} text-white`}>
                        {campaign.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{campaign.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {campaign.status === "draft" && (
                      <Button
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => updateStatus(campaign.id, "running")}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    )}
                    {campaign.status === "running" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(campaign.id, "paused")}
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pause
                      </Button>
                    )}
                    {campaign.status === "paused" && (
                      <Button
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => updateStatus(campaign.id, "running")}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Resume
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => navigate(`/dashboard/leads?campaign=${campaign.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Leads
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => updateStatus(campaign.id, "cancelled")} className="text-destructive">
                          <Square className="w-4 h-4 mr-2" />
                          Stop Campaign
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deleteCampaign(campaign.id)} className="text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Progress</span>
                    <span>{campaign.stats?.contacted || 0} / {campaign.stats?.total_contacts || 0} contacted</span>
                  </div>
                  <Progress value={getProgress(campaign.stats)} className="h-2" />
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-2xl font-bold text-emerald-500">{campaign.stats?.answered || 0}</p>
                    <p className="text-xs text-muted-foreground">Answered</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-2xl font-bold text-amber-500">{campaign.stats?.qualified || 0}</p>
                    <p className="text-xs text-muted-foreground">Qualified</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-2xl font-bold text-blue-500">{campaign.stats?.no_answer || 0}</p>
                    <p className="text-xs text-muted-foreground">No Answer</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-2xl font-bold text-purple-500">{campaign.stats?.converted || 0}</p>
                    <p className="text-xs text-muted-foreground">Converted</p>
                  </div>
                </div>

                {/* Meta Info */}
                <div className="flex items-center gap-6 mt-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {agents.find(a => a.id === campaign.agent_id)?.name || "Unknown Agent"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {campaign.calling_hours_start} - {campaign.calling_hours_end}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription>Set up a new outbound calling campaign</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign Name *</Label>
              <Input
                placeholder="e.g., Q1 Sales Outreach"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                data-testid="campaign-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of this campaign..."
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
              />
            </div>
            <div>
              <Label>AI Agent *</Label>
              <Select
                value={newCampaign.agent_id}
                onValueChange={(val) => setNewCampaign({ ...newCampaign, agent_id: val })}
              >
                <SelectTrigger data-testid="campaign-agent-select">
                  <SelectValue placeholder="Select agent..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name} - {agent.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact List *</Label>
              <Select
                value={newCampaign.list_id}
                onValueChange={(val) => setNewCampaign({ ...newCampaign, list_id: val })}
              >
                <SelectTrigger data-testid="campaign-list-select">
                  <SelectValue placeholder="Select contact list..." />
                </SelectTrigger>
                <SelectContent>
                  {lists.map((list) => (
                    <SelectItem key={list.id} value={list.id}>
                      {list.name} ({list.contact_count} contacts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lists.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No contact lists yet.{" "}
                  <button
                    className="text-primary hover:underline"
                    onClick={() => { setShowCreateDialog(false); navigate("/dashboard/contacts"); }}
                  >
                    Create one first
                  </button>
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Call Hours Start</Label>
                <Input
                  type="time"
                  value={newCampaign.calling_hours_start}
                  onChange={(e) => setNewCampaign({ ...newCampaign, calling_hours_start: e.target.value })}
                />
              </div>
              <div>
                <Label>Call Hours End</Label>
                <Input
                  type="time"
                  value={newCampaign.calling_hours_end}
                  onChange={(e) => setNewCampaign({ ...newCampaign, calling_hours_end: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Calls per Minute</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={newCampaign.calls_per_minute}
                  onChange={(e) => setNewCampaign({ ...newCampaign, calls_per_minute: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Max Attempts</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={newCampaign.max_attempts}
                  onChange={(e) => setNewCampaign({ ...newCampaign, max_attempts: parseInt(e.target.value) })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createCampaign} data-testid="save-campaign-btn">Create Campaign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
